import React, {FC, PropsWithChildren, useState} from 'react';
import styled, {css} from 'styled-components';

import {useTimeout} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {blendColors} from '../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {
  commonButtonStyles,
  roundedMainButtonContentStyles,
} from '../../../../libs/shared/ui/src/components/buttons/buttonContent';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface AnimatedCheckmarkButtonProps {
  onClick: () => void;
  interactionId?: string;
}

/*
 * Constants.
 */

const animationDuration = 2000; // ms
const transitionDuration = 0.35; // seconds

/*
 * Styles.
 */

interface BooleanStyleProps {
  $wasJustSelected: boolean;
}

const StyledCheckmarkButtonContainer = styled(ChromelessButton)<BooleanStyleProps>`
  ${roundedMainButtonContentStyles[VisualSizesEnum.SMALL]};
  ${commonButtonStyles}
  position: relative;
  border-radius: 100px;
  border: 1px solid ${(p) => p.theme.alphas.black30};
  transition-timing-function: ease-in;
  transition: background-color ${transitionDuration}s, border ${transitionDuration}s;

  &:hover {
    background-color: ${(p) => blendColors(p.theme.foreground, p.theme.alphas.gray10)};
  }

  ${(p) => p.$wasJustSelected && addWasJustSelectedStyles()}
`;

const greenBackgroundColor = (theme: UserTheme) => theme.palette.green.shade10;
function addWasJustSelectedStyles() {
  return css`
    background-color: ${(p) => greenBackgroundColor(p.theme)};
    border: 1px solid ${(p) => p.theme.palette[PaletteColorsEnum.GREEN].shade30};

    &:hover {
      background-color: ${(p) => greenBackgroundColor(p.theme)};
    }
  `;
}

const StyledCheckmarkDiv = styled.div<BooleanStyleProps>`
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -7px;
  margin-top: -8px;

  transition-timing-function: ease-in;
  transition: opacity ${transitionDuration}s;
  opacity: ${(p) => (p.$wasJustSelected ? 1 : 0)};
`;

const StyledContentDiv = styled.div<BooleanStyleProps>`
  transition-timing-function: ease-in;
  transition: opacity ${transitionDuration}s;
  opacity: ${(p) => (p.$wasJustSelected ? 0 : 1)};
`;

/*
 * Component.
 */

export const AnimatedCheckmarkButton: FC<PropsWithChildren<AnimatedCheckmarkButtonProps>> = (props) => {
  const theme = useUserTheme();
  const [wasJustCopied, setWasJustCopied] = useState(false);
  const [safeSetTimeout] = useTimeout();

  const onClick = () => {
    setWasJustCopied(true);

    safeSetTimeout(() => {
      setWasJustCopied(false);
    }, animationDuration);

    props.onClick();
  };

  return (
    <StyledCheckmarkButtonContainer
      $wasJustSelected={wasJustCopied}
      onClick={onClick}
      interactionId={props.interactionId}
    >
      <StyledContentDiv $wasJustSelected={wasJustCopied}>{props.children}</StyledContentDiv>
      <StyledCheckmarkDiv $wasJustSelected={wasJustCopied}>
        <Icon name="checkmarkBoxSmall" color={theme.palette[PaletteColorsEnum.GREEN].shade40} />
      </StyledCheckmarkDiv>
    </StyledCheckmarkButtonContainer>
  );
};
